'use client'

import * as React from 'react'

// The three KYC dashboard personas (TDD §7.1). This is a free demo persona
// switch — independent of the signed-in user's Keycloak role.
export type KycRole =
  | 'operations_manager'
  | 'compliance_officer'
  | 'relationship_manager'

export const KYC_ROLES: KycRole[] = [
  'operations_manager',
  'compliance_officer',
  'relationship_manager',
]

const DEFAULT_ROLE: KycRole = 'operations_manager'
const STORAGE_KEY = 'kyc.role'

interface RoleContextValue {
  role: KycRole
  setRole: (role: KycRole) => void
}

const RoleContext = React.createContext<RoleContextValue | undefined>(undefined)

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const [role, setRoleState] = React.useState<KycRole>(DEFAULT_ROLE)

  // Hydrate from localStorage on mount (client-only — avoids SSR mismatch).
  React.useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      if (stored && KYC_ROLES.includes(stored as KycRole)) {
        setRoleState(stored as KycRole)
      }
    } catch {
      // localStorage unavailable — fall back to the default role.
    }
  }, [])

  const setRole = React.useCallback((next: KycRole) => {
    setRoleState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Non-fatal: persistence is best-effort.
    }
  }, [])

  const value = React.useMemo(() => ({ role, setRole }), [role, setRole])

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>
}

export function useRole(): RoleContextValue {
  const ctx = React.useContext(RoleContext)
  if (!ctx) {
    throw new Error('useRole must be used within a RoleProvider')
  }
  return ctx
}
